"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { MapPin, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CITIES = ["Walnut Creek", "Danville", "San Ramon", "Discovery Bay", "Lafayette", "Orinda", "Moraga", "Dublin", "Pleasanton", "Livermore", "Concord", "Clayton", "Pleasant Hill", "Martinez", "Brentwood", "Antioch"];
const MAP_POINTS = [{
  x: 45,
  y: 30,
  id: "wc"
}, {
  x: 48,
  y: 45,
  id: "dv"
}, {
  x: 50,
  y: 60,
  id: "sr"
}, {
  x: 35,
  y: 35,
  id: "lf"
}, {
  x: 25,
  y: 38,
  id: "or"
}, {
  x: 30,
  y: 45,
  id: "mo"
}, {
  x: 95,
  y: 35,
  id: "db"
}, {
  x: 60,
  y: 75,
  id: "pl"
}, {
  x: 75,
  y: 78,
  id: "lv"
}, {
  x: 50,
  y: 15,
  id: "mt"
}, {
  x: 55,
  y: 20,
  id: "ph"
}, {
  x: 60,
  y: 25,
  id: "cc"
}, {
  x: 65,
  y: 28,
  id: "cl"
}, {
  x: 80,
  y: 35,
  id: "bw"
}, {
  x: 75,
  y: 25,
  id: "an"
}];
const MAP_LINKS = [{
  source: "wc",
  target: "dv"
}, {
  source: "dv",
  target: "sr"
}, {
  source: "sr",
  target: "db"
}, {
  source: "db",
  target: "pl"
}, {
  source: "pl",
  target: "lv"
}, {
  source: "wc",
  target: "lf"
}, {
  source: "lf",
  target: "or"
}, {
  source: "lf",
  target: "mo"
}, {
  source: "mo",
  target: "or"
}, {
  source: "wc",
  target: "ph"
}, {
  source: "ph",
  target: "mt"
}, {
  source: "ph",
  target: "cc"
}, {
  source: "cc",
  target: "cl"
}, {
  source: "cc",
  target: "an"
}, {
  source: "an",
  target: "bw"
}, {
  source: "wc",
  target: "cc"
}, {
  source: "sr",
  target: "lv"
}];
export default function WhereTheMagicTravels() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({
    width: 800,
    height: 400
  });
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    if (!svgRef.current) return;
    const updateDimensions = () => {
      if (svgRef.current) {
        setDimensions({
          width: svgRef.current.clientWidth,
          height: svgRef.current.clientHeight
        });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);
  const padding = 40;
  const xScale = (val: number) => {
    return padding + val / 100 * (dimensions.width - 2 * padding);
  };
  const yScale = (val: number) => {
    return padding + val / 100 * (dimensions.height - 2 * padding);
  };
  const particles = Array.from({
    length: 20
  }, (_, i) => ({
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2,
    key: i
  }));
  return <section id="where-the-magic-travels" className="relative w-full py-24 overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-30">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        
        {}
        <div className={cn("text-center pt-16 mb-16 transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-secondary animate-pulse" />
            <span className="text-primary font-medium tracking-widest uppercase text-xl md:text-3xl">Service Area</span>
            <Sparkles className="w-5 h-5 text-secondary animate-pulse" />
          </div>
          <h2 className="text-5xl md:text-6xl italic mb-6">
            Where the Magic Travels
          </h2>
          <p className="max-w-4xl mx-auto text-2xl !text-[hsl(0,0%,100%)] !italic">Bringing enchantment directly to your doorstep. <br />We proudly serve Contra Costa County and the entire San Francisco Bay Area.</p>
        </div>

        {}
        <div className={cn("relative w-full max-w-4xl mx-auto aspect-[16/9] md:aspect-[2/1] mb-16 bg-card/30 rounded-2xl border border-secondary/20 shadow-sm backdrop-blur-sm transition-all duration-1000 delay-300 transform", isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95")}>
          <svg ref={svgRef} className="w-full h-full" style={{
          overflow: 'visible'
        }}>
            {}
            <g className="links">
              {MAP_LINKS.map((link, i) => {
              const source = MAP_POINTS.find(p => p.id === link.source);
              const target = MAP_POINTS.find(p => p.id === link.target);
              if (!source || !target) return null;
              return <line key={`link-${i}`} x1={xScale(source.x)} y1={yScale(source.y)} x2={xScale(target.x)} y2={yScale(target.y)} stroke="var(--color-secondary)" strokeWidth={1} strokeOpacity={0.5} strokeDasharray="4,4" />;
            })}
            </g>

            {}
            <g className="nodes">
              {MAP_POINTS.map((point, i) => {
              const duration = `${2 + Math.random()}s`;
              return <g key={`node-${point.id}`} transform={`translate(${xScale(point.x)},${yScale(point.y)})`}>
                    {}
                    <circle r={4} fill="var(--color-secondary)" opacity={0.2}>
                      <animate attributeName="r" values="4;12;4" dur={duration} repeatCount="indefinite" />
                    </circle>

                    {}
                    <circle r={4} fill="var(--color-secondary)" opacity={0.2}>
                      <animate attributeName="opacity" values="0.2;0;0.2" dur={duration} repeatCount="indefinite" />
                    </circle>

                    {}
                    <circle r={3} fill="var(--color-secondary)" />
                  </g>;
            })}
            </g>

            {}
            <g className="particles">
              {particles.map(p => {
              const duration = `${3 + Math.random() * 2}s`;
              return <circle key={`particle-${p.key}`} cx={xScale(p.x)} cy={yScale(p.y)} r={p.size} fill="var(--color-secondary)" opacity={0.4}>
                    <animate attributeName="opacity" values="0;0.6;0" dur={duration} repeatCount="indefinite" />
                  </circle>;
            })}
            </g>
          </svg>
          
          {}
          <div className="absolute bottom-4 right-6 bg-background/80 backdrop-blur-md px-4 py-2 rounded-full border border-secondary/20 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
              <span className="text-xl font-medium text-primary tracking-wide">EAST BAY AREA</span>
            </div>
          </div>
        </div>

        {}
        <div className={cn("flex items-center justify-center gap-4 max-w-xs mx-auto mb-16 transition-all duration-1000 delay-500", isVisible ? "opacity-100" : "opacity-0")}>
          <div className="h-[1px] bg-gradient-to-r from-transparent via-secondary to-secondary flex-1 opacity-60" />
          <div className="w-1.5 h-1.5 rounded-full bg-secondary shadow-[0_0_8px_var(--color-secondary)]" />
          <div className="h-[1px] bg-gradient-to-l from-transparent via-secondary to-secondary flex-1 opacity-60" />
        </div>

        {}
        <div className={cn("grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4 max-w-5xl mx-auto transition-all duration-1000 delay-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          {CITIES.map((city, i) => <div key={city} data-index={i} className="group flex flex-col items-center text-center">
              <div className="mb-3 p-2 rounded-full bg-secondary/10 text-secondary group-hover:bg-secondary/20 group-hover:scale-110 transition-all duration-300">
                <MapPin className="w-4 h-4" />
              </div>
              <span className="text-primary font-medium text-xl tracking-wide transition-colors duration-300">
                {city}
              </span>
              
              {}
              <div className="w-12 h-[1px] bg-secondary/30 mt-4 md:hidden" />
            </div>)}
        </div>

        {}
        <div className={cn("mt-16 text-center transition-all duration-1000 delay-1000", isVisible ? "opacity-100" : "opacity-0")}>
          <p className="text-primary/80 text-xl mb-4">Don't see your city listed?</p>
          <Link to="#summon-the-magic" className="inline-flex items-center justify-center gap-2 text-primary hover:text-primary/80 text-xl font-medium underline underline-offset-4 transition-colors">Contact us to check availability.</Link>
        </div>

      </div>
    </section>;
}